import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Editor } from '@/components/ui/editor'
import type { FormDraft } from './formsSpaceConfig'

/**
 * The "Next" step of a one-form space (F-109): the title and an optional
 * introduction. The form is created as a draft right after the space, and the
 * person lands in its editor to add the fields.
 */
export const FormSingleStep: React.FC<{
  draft: FormDraft
  onChange: (draft: FormDraft) => void
  titleError?: string
  disabled?: boolean
}> = ({ draft, onChange, titleError, disabled = false }) => {
  const { t } = useTranslation('forms')
  /* The rich editor keeps the callback it was born with: it reads the latest draft through this. */
  const latest = React.useRef({ draft, onChange })
  latest.current = { draft, onChange }
  return (
    <div className="min-w-0 space-y-5" data-testid="form-single-step">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">{t('wizard.step')}</h3>
        <p className="text-xs text-gray-500">{t('wizard.stepHint')}</p>
      </div>
      <div className="grid gap-2">
        <label htmlFor="single-form-title" className="text-sm font-medium text-gray-700">
          {t('wizard.title')} <span className="text-red-500">*</span>
        </label>
        <Input id="single-form-title" value={draft.title} disabled={disabled} maxLength={200} aria-invalid={Boolean(titleError)}
          onChange={(e) => onChange({ ...draft, title: e.target.value })} placeholder={t('wizard.titlePlaceholder')} />
        {titleError && <p className="text-xs text-red-600">{titleError}</p>}
      </div>
      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">{t('wizard.description')}</label>
        <Editor content={draft.description}
          onChange={(description) => latest.current.onChange({ ...latest.current.draft, description })} className="min-h-[110px] sm:min-h-[140px]" />
      </div>
      <p className="text-xs text-gray-500">{t('wizard.bornDraft')}</p>
    </div>
  )
}
